<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { HOST_TULUM } from '@/utils/http'
import { MapSource, MapLayer } from '@/components/map'
import {
  getCaptureFootprint,
  getEvaluationStatus,
  useTaskingHover,
} from '@/composables/useTasking'
import { useLayerManager } from '@/composables/useLayerManager'
import { createLayerId, createSourceId } from '@/components/layers/types'
import { buildOrderTileUrl } from '@/lib/tileUrl'

const store = useStore()
const route = useRoute()

const emit = defineEmits(['isLoaded', 'statusUpdated'])
const props = defineProps({
  map: {
    type: Object,
    required: true
  },
  layerId: {
    type: String,
    required: true
  },
  beforeId: {
    type: String,
    required: true
  },
  groupId: {
    type: String,
    required: true
  },
  options: {
    type: Object,
    required: false,
    default: () => ({})
  },
  layer: {
    type: Object,
    required: false,
    default: () => ({})
  },
  isVisible: {
    type: Boolean,
    default: true
  }
})

const mapRef = computed(() => props.map)
const layerManager = useLayerManager(mapRef)

const currentStatus = ref(props.layer?.properties?.layer?.status ?? props.layer?.properties?.status ?? 1)
const pollingInterval = ref(null)
const orgId = computed(() => route.params.org_id)

// Get layer UUID for store lookups
const layerUuid = computed(() => props.layer?.uuid)

// Get captures from layer data
const captures = computed(() => {
  return props.layer?.properties?.layer?.captures || []
})

// Get hidden captures directly from store to ensure reactivity
const hiddenCaptures = computed(() => {
  if (!orgId.value || !layerUuid.value) return []
  const storeLayer = store.getters['layers/getOrgLayerById'](orgId.value, layerUuid.value)
  return storeLayer?.properties?.hiddenCaptures || []
})

// Map captures to their hi-res orders
const captureOrderMap = computed(() => {
  const orders = props.layer?.properties?.layer?.orders || []
  const map = {}
  for (const order of orders) {
    const captureId = order.data?.capture_id
    if (captureId) map[captureId] = order
  }
  return map
})

// Guard to ensure setDefaultHiddenCaptures only runs once
const defaultsInitialized = ref(false)

// IDs of captures that are not fulfilling (used to hide non-fulfilling by default)
const nonFulfillingCaptureIds = computed(() => {
  return captures.value.filter(c => c.fulfilling !== true).map(c => c.id)
})

// Set default hidden captures to non-fulfilling captures
const setDefaultHiddenCaptures = () => {
  if (!orgId.value || !layerUuid.value) return
  store.dispatch('layers/updateLayerProperties', {
    orgId: orgId.value,
    layerId: layerUuid.value,
    properties: { hiddenCaptures: nonFulfillingCaptureIds.value }
  })
}

const { hoveredCaptureId, setHoveredCapture, clearHoveredCapture } = useTaskingHover()

// Source and layer IDs for map
const footprintSourceId = computed(() => createSourceId(props.groupId, 'tasking-footprints'))
const fillLayerId = computed(() => createLayerId(props.groupId, 'fill'))
const lineLayerId = computed(() => createLayerId(props.groupId, 'line'))

// Convert captures with footprints to GeoJSON FeatureCollection
const capturesGeoJSON = computed(() => {
  const features = captures.value
    .filter(capture => getCaptureFootprint(capture) && !hiddenCaptures.value.includes(capture.id))
    .map(capture => ({
      type: 'Feature',
      geometry: getCaptureFootprint(capture),
      properties: {
        code: capture.id,
        id: capture.id,
        strip_id: capture.strip_id,
        status: capture.status,
        evaluation_status: getEvaluationStatus(capture),
        cloud_cover: capture.cloud_cover,
        capture_time: capture.published_time || capture.acquired_time || capture.capture_time,
        isInvalid: getEvaluationStatus(capture) === 'INVALID'
      }
    }))

  return {
    type: 'FeatureCollection',
    features
  }
})

// Check if there are any captures with footprints
const hasFootprints = computed(() => {
  return captures.value.some(capture => getCaptureFootprint(capture))
})

// Get ALL captures with imagery tiles (regardless of hidden state)
const allCapturesWithImagery = computed(() => {
  return captures.value
    .filter(capture => {
      const itemIds = capture.item_ids || capture.scene_ids || []
      return itemIds.length > 0
    })
    .map(capture => {
      const itemIds = capture.item_ids || capture.scene_ids || []
      const itemIdsStr = itemIds.join(',')
      const order = captureOrderMap.value[capture.id]
      const isHiRes = order?.status === 0 && order?.planet_order_id

      // Suborders created via /from-capture always use product_bundle 'visual'
      // (see apiv3/app/routers/layers.py::create_order_from_capture). Pass an
      // explicit bundle so direct-S3 URLs target tiles/visual/... rather than
      // the bundleless legacy path.
      const hiResUrl = isHiRes
        ? buildOrderTileUrl(orgId.value, order.code, order, order?.data?.product_bundle || 'visual')
        : null
      return {
        id: capture.id,
        strip_id: capture.strip_id,
        item_ids: itemIdsStr,
        isHiRes,
        tileUrl: hiResUrl
          ?? `${HOST_TULUM}/tasking/${orgId.value}/tiles/${itemIdsStr}/{z}/{x}/{y}.png`,
        isInvalid: getEvaluationStatus(capture) === 'INVALID'
      }
    })
})

// Check if there are any captures with imagery
const hasImagery = computed(() => {
  return allCapturesWithImagery.value.length > 0
})

const fetchLayerStatus = async () => {
  if (!props.layer?.uuid || !orgId.value) return

  try {
    await store.dispatch('layers/fetchOrgLayers', { orgId: orgId.value })

    // Read updated status from store
    const storeLayer = store.getters['layers/getOrgLayerById'](orgId.value, props.layer.uuid)
    const newStatus = storeLayer?.properties?.layer?.status
    if (newStatus != null && newStatus !== currentStatus.value) {
      currentStatus.value = newStatus
      emit('statusUpdated', newStatus)

      // Stop polling if status is complete/failed AND no active orders
      if ((newStatus === 0 || newStatus < 0) && !hasActiveOrders.value) {
        stopPolling()
      }
    }
  } catch (error) {
    console.error('Error fetching layer status:', error)
  }
}

const hasActiveOrders = computed(() => {
  const orders = props.layer?.properties?.layer?.orders || []
  return orders.some(o => o.status > 0 || o.status === 100)
})

const startPolling = () => {
  // Poll layer status if in progress or if there are active orders
  const status = parseInt(currentStatus.value)
  if (status > 0 || status === 100 || hasActiveOrders.value) {
    // Poll layer status every 60 seconds
    pollingInterval.value = setInterval(fetchLayerStatus, 60000)
    fetchLayerStatus()
  }
}

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
  }
}

// Track added raster sources/layers for cleanup
const addedRasterLayers = ref([])

// Get layer ID for a capture
const getRasterLayerId = (captureId) => createLayerId(props.groupId, `raster-${captureId}`)
const getRasterSourceId = (captureId) => createSourceId(props.groupId, `raster-source-${captureId}`)

// Sync raster layers with current captures (add new, remove stale, keep existing)
const initializeRasterLayers = () => {
  if (!props.map || !props.isVisible) return

  const currentCaptureIds = new Set(allCapturesWithImagery.value.map(c => c.id))
  const existingCaptureIds = new Set(addedRasterLayers.value.map(l => l.captureId))

  // Remove layers for captures that no longer exist
  const layersToRemove = addedRasterLayers.value.filter(l => !currentCaptureIds.has(l.captureId))
  layersToRemove.forEach(({ sourceId, layerId }) => {
    layerManager.unregisterLayer(props.groupId, layerId)
    if (props.map.getLayer(layerId)) {
      props.map.removeLayer(layerId)
    }
    if (props.map.getSource(sourceId)) {
      props.map.removeSource(sourceId)
    }
  })

  // Keep only layers that still exist
  addedRasterLayers.value = addedRasterLayers.value.filter(l => currentCaptureIds.has(l.captureId))

  // Add layers for new captures only
  allCapturesWithImagery.value.forEach((capture) => {
    if (existingCaptureIds.has(capture.id)) return // Already exists

    const sourceId = getRasterSourceId(capture.id)
    const layerId = getRasterLayerId(capture.id)
    const isHidden = hiddenCaptures.value.includes(capture.id)

    // Add source
    if (!props.map.getSource(sourceId)) {
      props.map.addSource(sourceId, {
        type: 'raster',
        tiles: [capture.tileUrl],
        tileSize: 256,
        minzoom: 0,
        maxzoom: 22
      })
    }

    // Add layer
    if (!props.map.getLayer(layerId)) {
      const beforeLayerId = props.map.getLayer(lineLayerId.value) ? lineLayerId.value : props.beforeId
      props.map.addLayer({
        id: layerId,
        type: 'raster',
        source: sourceId,
        metadata: {
          groupId: props.groupId
        },
        paint: {
          'raster-opacity': props.layer?.properties?.opacity ?? 1
        },
        layout: {
          visibility: props.isVisible && !isHidden ? 'visible' : 'none'
        }
      }, beforeLayerId)
    }

    addedRasterLayers.value.push({ sourceId, layerId, captureId: capture.id })
    layerManager.registerLayer(props.groupId, layerId)
  })
}

// Toggle visibility of individual capture layers (called when hiddenCaptures changes)
const updateCaptureVisibility = () => {
  if (!props.map) return

  addedRasterLayers.value.forEach(({ layerId, captureId }) => {
    if (props.map.getLayer(layerId)) {
      const isHidden = hiddenCaptures.value.includes(captureId)
      const visibility = props.isVisible && !isHidden ? 'visible' : 'none'
      props.map.setLayoutProperty(layerId, 'visibility', visibility)
    }
  })
}

// Watch for captures list changes (add/remove layers as needed)
watch(allCapturesWithImagery, () => {
  initializeRasterLayers()
})

// Watch for layer visibility changes - need to initialize if layers don't exist yet
watch(() => props.isVisible, (isVisible) => {
  if (isVisible) {
    if (!defaultsInitialized.value) {
      setDefaultHiddenCaptures()
      defaultsInitialized.value = true
    }
    if (addedRasterLayers.value.length === 0) {
      initializeRasterLayers()
    } else {
      updateCaptureVisibility()
    }
  } else {
    updateCaptureVisibility()
  }
})

// Watch for hiddenCaptures changes (just toggle visibility)
watch(hiddenCaptures, () => {
  updateCaptureVisibility()
}, { deep: true })

// Watch for order completion — update raster tile URLs on the map
watch(captureOrderMap, (newMap, oldMap) => {
  for (const [captureId, order] of Object.entries(newMap)) {
    if (order.status === 0 && oldMap?.[captureId]?.status !== 0) {
      const sourceId = getRasterSourceId(captureId)
      const capture = allCapturesWithImagery.value.find(c => c.id === captureId)
      if (capture && props.map?.getSource(sourceId)) {
        props.map.getSource(sourceId).setTiles([capture.tileUrl])
      }
    }
  }
}, { deep: true })

// --- Hover feature-state management ---
let previousHoveredId = null

const applyHoverState = (captureId, hovered) => {
  if (!props.map || !props.map.getSource(footprintSourceId.value)) return
  props.map.setFeatureState(
    { source: footprintSourceId.value, id: captureId },
    { hover: hovered }
  )
}

const onFillMouseMove = (e) => {
  if (!e.features || e.features.length === 0) return
  const feature = e.features[0]
  const captureId = feature.properties?.code
  if (!captureId || captureId === previousHoveredId) return

  // Clear previous hover
  if (previousHoveredId) {
    applyHoverState(previousHoveredId, false)
  }

  previousHoveredId = captureId
  applyHoverState(captureId, true)
  setHoveredCapture(captureId)
}

const onFillMouseLeave = () => {
  if (previousHoveredId) {
    applyHoverState(previousHoveredId, false)
    previousHoveredId = null
  }
  clearHoveredCapture()
}

// Watch shared hoveredCaptureId for card-initiated hover
watch(hoveredCaptureId, (newId, oldId) => {
  if (oldId && oldId !== previousHoveredId) {
    applyHoverState(oldId, false)
  }
  if (newId) {
    applyHoverState(newId, true)
    previousHoveredId = newId
  } else if (previousHoveredId) {
    applyHoverState(previousHoveredId, false)
    previousHoveredId = null
  }
})

// Re-apply hover state after source data updates (setData resets feature state)
watch(capturesGeoJSON, () => {
  if (hoveredCaptureId.value) {
    // Use nextTick-like delay to let Mapbox process the data update
    setTimeout(() => {
      applyHoverState(hoveredCaptureId.value, true)
    }, 0)
  }
})

const setupMapHoverListeners = () => {
  if (!props.map) return
  props.map.on('mousemove', fillLayerId.value, onFillMouseMove)
  props.map.on('mouseleave', fillLayerId.value, onFillMouseLeave)
}

const cleanupMapHoverListeners = () => {
  if (!props.map) return
  props.map.off('mousemove', fillLayerId.value, onFillMouseMove)
  props.map.off('mouseleave', fillLayerId.value, onFillMouseLeave)
}

onMounted(async () => {
  emit('isLoaded', true)
  startPolling()

  // Set default hidden captures when layer is already visible on mount
  if (props.isVisible) {
    setDefaultHiddenCaptures()
    defaultsInitialized.value = true
  }

  // Register fill/line layers with layer manager for z-index ordering
  layerManager.registerLayer(props.groupId, fillLayerId.value)
  layerManager.registerLayer(props.groupId, lineLayerId.value)

  // Add raster layers after map is ready
  if (props.map) {
    if (props.map.isStyleLoaded()) {
      initializeRasterLayers()
      setupMapHoverListeners()
    } else {
      props.map.once('idle', () => {
        initializeRasterLayers()
        setupMapHoverListeners()
      })
    }
  }
})

onUnmounted(() => {
  stopPolling()
  cleanupMapHoverListeners()

  // Unregister fill/line layers from layer manager
  layerManager.unregisterLayer(props.groupId, fillLayerId.value)
  layerManager.unregisterLayer(props.groupId, lineLayerId.value)

  // Clean up raster layers
  addedRasterLayers.value.forEach(({ sourceId, layerId }) => {
    layerManager.unregisterLayer(props.groupId, layerId)
    if (props.map?.getLayer(layerId)) {
      props.map.removeLayer(layerId)
    }
    if (props.map?.getSource(sourceId)) {
      props.map.removeSource(sourceId)
    }
  })
})
</script>

<template>
  <div class="space-y-2">
    <!-- Capture footprints on map (outline only) -->
    <MapSource
      v-if="hasFootprints && props.isVisible"
      :map="map"
      type="geojson"
      :id="footprintSourceId"
      :data="capturesGeoJSON"
    >
      <!-- Invisible fill layer for hover hit detection -->
      <MapLayer
        :map="map"
        :id="fillLayerId"
        :source="footprintSourceId"
        type="fill"
        :before-id="beforeId"
        :group-id="groupId"
        :interactive="false"
        :options="{
          metadata: { opacityIgnore: true },
          paint: {
            'fill-opacity': 0
          },
          layout: {
            visibility: isVisible ? 'visible' : 'none'
          }
        }"
      />
      <!-- Line layer for capture footprint outlines (visible only on hover) -->
      <MapLayer
        :map="map"
        :id="lineLayerId"
        :source="footprintSourceId"
        type="line"
        :before-id="beforeId"
        :group-id="groupId"
        :interactive="false"
        :options="{
          metadata: { opacityIgnore: true },
          paint: {
            'line-color': [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
              '#0059FF',
              ['case', ['get', 'isInvalid'], '#ef4444', '#3b82f6']
            ],
            'line-width': [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
              3,
              2
            ],
            'line-opacity': [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
              1,
              0
            ]
          },
          layout: {
            visibility: isVisible ? 'visible' : 'none'
          }
        }"
      />
    </MapSource>

  </div>
</template>
